import CalendarDates from 'calendar-dates';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { DateTime } from 'luxon';
import { Col, Row } from 'react-styled-flexboxgrid';
import classNames from 'classnames';

import IconButton from 'shared-assets/components/IconButton';
import colors from 'shared-assets/lib/colors';

const calendarDates = new CalendarDates();

const Wrapper = styled.div`
  width: 100%;

  && {
    table {
      margin-top: 0.5rem;
      width: 100%;
      height: 100%;
      text-align: center;
      font-size: 0.8125rem;
      border-collapse: collapse;

      th {
        display: table-cell;
        font-weight: 400;
        color: ${colors.light};
        text-align: center;
      }

      th,
      .previous,
      .next {
        button:not(.selected) {
          color: ${colors.light};
        }
      }

      tbody {
        display: table-row-group;

        th {
          display: table-cell;
        }
        ,
        tr {
          display: table-row;
        }
        ,
        td {
          display: table-cell;
          padding: 0.25rem 0;

          button {
            font-size: 0.8125rem;
            font-weight: 600;
          }
        }
      }
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TodayButton = styled.button`
  background: transparent;
  border: none;
  color: ${colors.primary};
  outline: none;
  cursor: pointer;
  && {
    font-size: 0.8125rem;
  }
`;

const DateButton = styled.button`
  border: none;
  background: transparent;
  color: ${colors.secondary};
  outline: none;
  font-size: 0.8125rem;
  margin: 0;
  border-radius: 2px;
  cursor: pointer;
  padding: 0px 7px;

  &:hover {
    background: rgba(${colors.primaryRGB.join(', ')}, 0.1);
  }

  &.today {
    color: ${colors.primary};
    background: rgba(${colors.primaryRGB.join(', ')}, 0.16);
  }

  &.selected {
    color: #fff;
    background: ${colors.primary};
  }
`;

const NextPrevButton = styled(IconButton)`
  &:disabled {
    pointer-events: none;
    filter: opacity(0.5);
  }
`;

class Calendar extends React.PureComponent {
  constructor(props) {
    super(props);
    this.today = DateTime.fromJSDate(new Date(), {
      zone: props.timeZone,
    }).toISODate();

    let selectedDate = props.date
      ? DateTime.fromISO(props.date, {
          zone: props.timeZone,
        }).toISODate()
      : this.today;

    this.state = {
      today: this.today,
      selectedDate,
      selectedMonth: selectedDate,
    };
    this.yearOptions = [];
    let currentYear = DateTime.fromISO(this.today).year;
    for (
      let i = currentYear + this.props.numFutureYears;
      i > currentYear;
      i--
    ) {
      this.yearOptions.push(i);
    }

    for (let i = currentYear; i >= currentYear - this.props.numPastYears; i--) {
      this.yearOptions.push(i);
    }
    this.monthOptions = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    this.days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  }

  static getDerivedStateFromProps(props) {
    return !!props.date
      ? {
          selectedDate: DateTime.fromISO(props.date, {
            zone: props.timeZone,
          }).toISODate(),
        }
      : null;
  }

  componentDidMount() {
    calendarDates
      .getMatrix(DateTime.fromISO(this.state.selectedDate).toJSDate())
      .then(dates => {
        this.setState({
          dates,
        });
      });
  }

  changeMonth = direction => {
    const newDate = this.getNextOrPrevMonthDate(direction);

    if (newDate === '') {
      return;
    }

    calendarDates
      .getMatrix(DateTime.fromISO(newDate).toJSDate())
      .then(dates => {
        this.setState({
          selectedMonth: newDate,
          dates,
        });
      });
  };

  goBackToToday = () => {
    this.props.onChange({ iso: this.today });
    calendarDates
      .getMatrix(DateTime.fromISO(this.today).toJSDate())
      .then(dates => {
        this.setState({
          selectedDate: this.today,
          selectedMonth: this.today,
          dates,
        });
      });
  };

  handleMonthChange = event => {
    const newMonth = parseInt(event.target.value);
    const newDate = DateTime.fromISO(
      this.state.selectedMonth || this.state.selectedDate,
      {
        zone: this.props.timeZone,
      },
    )
      .set({ month: newMonth })
      .toISODate();
    calendarDates
      .getMatrix(DateTime.fromISO(newDate).toJSDate())
      .then(dates => {
        this.setState({
          selectedMonth: newDate,
          dates,
        });
      });
  };

  handleYearChange = event => {
    const newYear = parseInt(event.target.value);
    const newDate = DateTime.fromISO(
      this.state.selectedMonth || this.state.selectedDate,
      {
        zone: this.props.timeZone,
      },
    )
      .set({ year: newYear })
      .toISODate();
    calendarDates
      .getMatrix(DateTime.fromISO(newDate).toJSDate())
      .then(dates => {
        this.setState({
          selectedMonth: newDate,
          dates,
        });
      });
  };

  handleDate = date => {
    if (this.isDateOnYearRange(DateTime.fromISO(date.iso))) {
      this.props.onChange(date);
      this.setState({
        selectedDate: DateTime.fromISO(date.iso, {
          zone: this.props.timeZone,
        }).toISODate(),
      });
    }
  };

  isTodayButtonVisible() {
    if (
      this.state.selectedMonth &&
      this.state.selectedMonth !== this.state.today
    ) {
      return true;
    }

    if (this.state.selectedDate !== this.state.today) {
      return true;
    }

    return false;
  }

  getNextOrPrevMonthDate = direction => {
    const newDate = DateTime.fromISO(
      this.state.selectedMonth || this.state.selectedDate,
      {
        zone: this.props.timeZone,
      },
    )
      [direction]({ months: 1 })
      .toISODate();

    const conditionDate = DateTime.fromISO(newDate);

    if (this.isDateOnYearRange(conditionDate)) {
      return newDate;
    }
    return '';
  };

  isDateOnYearRange = date => {
    if (this.props.yearLimitEnabled) {
      if (!this.yearOptions.includes(date.year)) {
        return false;
      }
    }
    return true;
  };

  render() {
    return this.state.dates ? (
      <Wrapper data-testid="calendar">
        <Header>
          <Col>
            <Row middle="xs">
              <NextPrevButton
                onClick={() => this.changeMonth('minus')}
                icon="angle-bracket-left"
                tip="Previous month"
                data-testid="prev-month"
                disabled={
                  this.getNextOrPrevMonthDate('minus') === '' ||
                  this.props?.isDisabled
                }
                tabIndex="-1"
              />
              <NextPrevButton
                onClick={() => this.changeMonth('plus')}
                icon="angle-bracket-right"
                tip="Next month"
                data-testid="next-month"
                disabled={
                  this.getNextOrPrevMonthDate('plus') === '' ||
                  this.props?.isDisabled
                }
                tabIndex="-1"
              />
              {this.props.showMonthAndYearSelection ? (
                <React.Fragment>
                  <select
                    data-testid="current-month"
                    style={{
                      paddingLeft: '0.25rem',
                      fontSize: '0.8125rem',
                      width: '3.2rem',
                    }}
                    value={
                      DateTime.fromISO(
                        this.state.selectedMonth || this.state.selectedDate,
                        {
                          zone: this.props.timeZone,
                        },
                      ).month
                    }
                    onChange={event => this.handleMonthChange(event)}
                    tabIndex="-1"
                  >
                    <For each="month" of={this.monthOptions} index="index">
                      <option key={month} value={index + 1}>
                        {month}
                      </option>
                    </For>
                  </select>
                  <select
                    data-testid="current-year"
                    style={{
                      paddingLeft: '0.25rem',
                      marginLeft: '0.1rem',
                      fontSize: '0.8125rem',
                      width: '3.2rem',
                    }}
                    value={
                      DateTime.fromISO(
                        this.state.selectedMonth || this.state.selectedDate,
                        {
                          zone: this.props.timeZone,
                        },
                      ).year
                    }
                    onChange={event => this.handleYearChange(event)}
                    tabIndex="-1"
                  >
                    <For each="year" of={this.yearOptions} index="index">
                      <option key={year} value={year}>
                        {year}
                      </option>
                    </For>
                  </select>
                </React.Fragment>
              ) : (
                <span
                  data-testid="current-month"
                  style={{ paddingLeft: '0.25rem', fontSize: '0.8125rem' }}
                >
                  {DateTime.fromISO(
                    this.state.selectedMonth || this.state.selectedDate,
                    {
                      zone: this.props.timeZone,
                    },
                  ).toLocaleString({ month: 'long', year: 'numeric' })}
                </span>
              )}
            </Row>
          </Col>
          <If condition={this.isTodayButtonVisible()}>
            <TodayButton
              type="button"
              data-testid="today-button"
              className="link"
              onClick={this.goBackToToday}
            >
              Today
            </TodayButton>
          </If>
        </Header>
        <table>
          <tbody>
            <tr>
              <For each="day" of={this.days} index="index">
                <th key={index}>{day}</th>
              </For>
            </tr>
            <For each="week" of={this.state.dates} index="index">
              <tr key={index}>
                <For each="date" of={week}>
                  <td
                    data-testid={'date-buttons-' + date.date}
                    cellPadding={0}
                    cellSpacing={0}
                    key={date.date}
                    className={classNames({
                      [date.type]: true,
                      [date.iso]: true,
                    })}
                  >
                    <DateButton
                      disabled={this.props?.isDisabled}
                      type="button"
                      className={classNames({
                        today: date.iso === this.today,
                        selected: date.iso === this.state.selectedDate,
                      })}
                      onClick={() => this.handleDate(date)}
                      tabIndex="-1"
                    >
                      {date.date}
                    </DateButton>
                  </td>
                </For>
              </tr>
            </For>
          </tbody>
        </table>
      </Wrapper>
    ) : null;
  }
}

Calendar.defaultProps = {
  isDisabled: false,
  date: undefined,
  timeZone: 'local',
  onChange: () => {},
  showMonthAndYearSelection: false,
  yearLimitEnabled: false,
  numFutureYears: 0,
  numPastYears: 50,
};

Calendar.propTypes = {
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func,
  timeZone: PropTypes.string,
  date: PropTypes.string,
  showMonthAndYearSelection: PropTypes.bool,
  yearLimitEnabled: PropTypes.bool, // this property won't affect anything if showMonthAndYearSelection is true. it has to be used in case dropdowns are disabled and you want years to be 'limitless'
  numFutureYears: PropTypes.number,
  numPastYears: PropTypes.number,
};

export default Calendar;
